import React, { useEffect, useMemo, useState } from "react";
import Button from "../../Components/Utils/Button/Button";
import Checkbox from "../../Components/Utils/Checkbox/Checkbox";
import FilterSearchBar from "../../Components/SearchBar/FilterSearchBar/FilterSearchBar";
import type { ISelectOption } from "../../Components/Analyse/CompactFilters/filterTypes";
import {
  getLibraryLinkQuestions,
  LibraryLinkTarget,
  LibraryLinkTargets,
  saveLibraryLink,
} from "../../API/Library/LibraryCommands";
import { applyQuestionTitles, libraryErrorMessage, libraryKindNoun, libraryProductLabel, libraryTargetNoun } from "./libraryUtils";
import { toast } from "react-toastify";
import LibraryDialog from "./LibraryDialog";
import styles from "./Library.module.css";

const filterOption = (value: string, label: string): ISelectOption => ({ value, label, required: false });
const emptyFilters: Record<string, ISelectOption[]> = { type: [], status: [] };

type LinkEvidenceDialogProps = {
  isVisible: boolean;
  portfolioId: string;
  clientId: string;
  links: LibraryLinkTargets | null;
  canEdit: boolean;
  onClose: () => void;
  onChange: (next: LibraryLinkTargets) => void;
};

const LinkEvidenceDialog = ({
  isVisible,
  portfolioId,
  clientId,
  links,
  canEdit,
  onClose,
  onChange,
}: LinkEvidenceDialogProps) => {
  const [query, setQuery] = useState("");
  const [selectedFilters, setSelectedFilters] = useState<Record<string, ISelectOption[]>>(emptyFilters);
  const [openAreaId, setOpenAreaId] = useState("");
  const [questionsByArea, setQuestionsByArea] = useState<Record<string, LibraryLinkTarget[]>>({});
  const [loadingArea, setLoadingArea] = useState("");
  const [busyKey, setBusyKey] = useState("");

  useEffect(() => {
    setQuestionsByArea({});
    setOpenAreaId("");
    setQuery("");
    setSelectedFilters(emptyFilters);
  }, [links?.fileId]);

  const typeOptions = useMemo(() => {
    const types: string[] = [];
    for (const area of links?.areas ?? []) {
      const type = area.type || "Evaluate";
      if (types.indexOf(type) === -1) types.push(type);
    }
    return types.map((type) => filterOption(type, libraryProductLabel(type)));
  }, [links]);

  const filterConfig = useMemo(() => [
    { key: "type", label: "Type", placeholder: "Select types", dataSource: "props" as const, data: typeOptions },
    {
      key: "status",
      label: "Status",
      placeholder: "Select status",
      dataSource: "props" as const,
      data: [filterOption("linked", "Linked"), filterOption("unlinked", "Not linked")],
    },
  ], [typeOptions]);

  const areas = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const types = (selectedFilters.type ?? []).map((option) => option.value);
    const statuses = (selectedFilters.status ?? []).map((option) => option.value);
    return (links?.areas ?? []).filter((area) => {
      const title = (area.areaTitle || area.areaId).toLowerCase();
      if (needle && title.indexOf(needle) === -1) return false;
      if (types.length > 0 && types.indexOf(area.type || "Evaluate") === -1) return false;
      if (statuses.length > 0) {
        const linked = !!area.areaLinked;
        if (!(linked && statuses.indexOf("linked") !== -1) && !(!linked && statuses.indexOf("unlinked") !== -1)) {
          return false;
        }
      }
      return true;
    });
  }, [links, query, selectedFilters]);

  const publish = (saved: LibraryLinkTargets) => {
    const known = Object.values(questionsByArea).flat();
    onChange({
      ...saved,
      links: applyQuestionTitles(saved.links ?? [], known),
    });
  };

  const selectedArea = (links?.areas ?? []).find((area) => area.areaId === openAreaId) ?? null;
  const selectedQuestions = selectedArea ? questionsByArea[selectedArea.areaId] ?? [] : [];
  const selectedGroups = selectedQuestions.reduce<Record<string, LibraryLinkTarget[]>>((grouped, question) => {
    const key = question.sectionTitle || "Questions";
    grouped[key] = [...(grouped[key] ?? []), question];
    return grouped;
  }, {});

  const toggleArea = async (area: LibraryLinkTarget, linked: boolean) => {
    if (!links || !canEdit) return;
    const key = `area:${area.areaId}`;
    setBusyKey(key);
    try {
      publish(await saveLibraryLink(portfolioId, clientId, {
        fileId: links.fileId,
        type: area.type || "Evaluate",
        areaId: area.areaId,
        linked,
      }));
      if (!linked && openAreaId === area.areaId) setOpenAreaId("");
    } catch (error) {
      toast.error(libraryErrorMessage(error, `That ${libraryKindNoun(area.type)} could not be updated.`));
    } finally {
      setBusyKey("");
    }
  };

  const openQuestions = async (area: LibraryLinkTarget) => {
    if (openAreaId === area.areaId) {
      setOpenAreaId("");
      return;
    }
    setOpenAreaId(area.areaId);
    if (questionsByArea[area.areaId] || !links) return;
    setLoadingArea(area.areaId);
    try {
      const next = await getLibraryLinkQuestions(portfolioId, links.fileId, area.areaId, clientId);
      setQuestionsByArea((current) => ({ ...current, [area.areaId]: next.questions }));
    } catch (error) {
      toast.error(libraryErrorMessage(error, `Those ${libraryTargetNoun(area.type, true)} could not be loaded.`));
    } finally {
      setLoadingArea("");
    }
  };

  const toggleQuestion = async (area: LibraryLinkTarget, question: LibraryLinkTarget, linked: boolean) => {
    if (!links || !canEdit) return;
    const key = `q:${area.areaId}:${question.questionId}`;
    setBusyKey(key);
    try {
      const saved = await saveLibraryLink(portfolioId, clientId, {
        fileId: links.fileId,
        type: area.type || "Evaluate",
        areaId: area.areaId,
        questionId: question.questionId,
        linked,
      });
      publish(saved);
      setQuestionsByArea((current) => ({
        ...current,
        [area.areaId]: (current[area.areaId] ?? []).map((item) =>
          item.questionId === question.questionId ? { ...item, linked } : item
        ),
      }));
    } catch (error) {
      toast.error(libraryErrorMessage(error, "That question link could not be updated."));
    } finally {
      setBusyKey("");
    }
  };

  return (
    <LibraryDialog isVisible={isVisible} title="Link evidence" size="wide" onClose={onClose}>
      <div className={styles.dialogBody}>
        <p className={styles.memberLine}>
          Link this file to a Diagnostic or Track Plan, then choose the criteria or objectives it supports.
        </p>
        <div className={styles.linkSearch}>
          <FilterSearchBar
            value={query}
            onChange={setQuery}
            placeholder="Search diagnostics and Track Plans"
            filters={filterConfig}
            selectedFilters={selectedFilters}
            onFilterChange={setSelectedFilters}
          />
        </div>
        <div className={`${styles.linkWorkspace} ${selectedArea ? styles.linkSplitOpen : ""}`}>
          <div className={styles.linkList}>
            {areas.length === 0 && (
              <p className={styles.meta}>No diagnostics or Track Plans match those filters.</p>
            )}
            {areas.map((area) => {
              const linked = !!area.areaLinked;
              const selected = openAreaId === area.areaId;
              return (
                <div
                  key={`${area.type}-${area.areaId}`}
                  className={`${styles.linkArea} ${selected ? styles.linkAreaSelected : ""}`}
                >
                  <div className={styles.linkAreaRow}>
                    <div className={styles.linkAreaTitle}>
                      <strong title={area.areaTitle || area.areaId}>{area.areaTitle || area.areaId}</strong>
                      <span className={styles.pill}>{libraryProductLabel(area.type)}</span>
                    </div>
                    <div className={styles.linkAreaActions}>
                      {canEdit && (
                        <Button
                          size="Small"
                          disabled={busyKey === `area:${area.areaId}`}
                          colour={linked ? "#fff" : undefined}
                          textColour={linked ? "#24394F" : undefined}
                          border={linked ? "#d5dde6" : undefined}
                          clickHandler={() => toggleArea(area, !linked)}
                        >
                          {linked ? "Unlink" : "Link"}
                        </Button>
                      )}
                      {linked && (
                        <Button
                          size="Small"
                          colour={selected ? undefined : "#fff"}
                          textColour={selected ? undefined : "#24394F"}
                          border={selected ? undefined : "#d5dde6"}
                          clickHandler={() => openQuestions(area)}
                        >
                          {selected
                            ? `Hide ${libraryTargetNoun(area.type, true)}`
                            : `Link to ${libraryTargetNoun(area.type, true)}`}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {selectedArea && (
            <aside className={styles.linkQuestionPane} aria-label={`Link ${libraryTargetNoun(selectedArea.type, true)}`}>
              <div className={styles.linkQuestionHeader}>
                <div className={styles.linkAreaTitle}>
                  <strong title={selectedArea.areaTitle || selectedArea.areaId}>
                    {selectedArea.areaTitle || selectedArea.areaId}
                  </strong>
                  <span className={styles.pill}>{libraryProductLabel(selectedArea.type)}</span>
                </div>
                <button
                  type="button"
                  className={styles.iconButton}
                  onClick={() => setOpenAreaId("")}
                  aria-label={`Close ${libraryTargetNoun(selectedArea.type, true)}`}
                >
                  <img src="/img/close.svg" alt="" />
                </button>
              </div>
              <div className={styles.questionList}>
                {loadingArea === selectedArea.areaId && (
                  <p className={styles.meta}>Loading {libraryTargetNoun(selectedArea.type, true)}…</p>
                )}
                {loadingArea !== selectedArea.areaId && selectedQuestions.length === 0 && (
                  <p className={styles.meta}>
                    No {libraryTargetNoun(selectedArea.type, true)} were found for this {libraryKindNoun(selectedArea.type)}.
                  </p>
                )}
                {Object.entries(selectedGroups).map(([section, items]) => (
                  <div key={section} className={styles.questionGroup}>
                    <h5 title={section}>{section}</h5>
                    {items.map((question) => {
                      const title = question.questionTitle || question.questionId;
                      return (
                        <div key={question.questionId} className={styles.questionRow} title={title}>
                          <Checkbox
                            value={question.linked}
                            disabled={!canEdit || busyKey === `q:${selectedArea.areaId}:${question.questionId}`}
                            clickHandler={(linked) => toggleQuestion(selectedArea, question, linked)}
                          />
                          <span>{title}</span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </aside>
          )}
        </div>
        <div className={styles.dialogActions}>
          <Button clickHandler={onClose}>Done</Button>
        </div>
      </div>
    </LibraryDialog>
  );
};

export default LinkEvidenceDialog;
